import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, Button, Flex, FormControl, Grid, Link, SlideFade, Textarea, useToast } from '@chakra-ui/react';
import { RegExpMatcher, englishDataset, englishRecommendedTransformers } from 'obscenity';

import { useGetUserQuery } from 'api/user';
import { useUpdateUserBioMutation } from 'api/user';

type FormData = {
  bio: string;
};

const EditBio = () => {
  const { data: user, isLoading: isUserLoading } = useGetUserQuery();
  const maxBioLength = 100;
  const {
    handleSubmit,
    register,
    getFieldState,
    watch,
    formState: { errors, isValid, isDirty, touchedFields },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      bio: user?.bio || '',
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [updateUser, updateUserReq] = useUpdateUserBioMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  const { isError: isUpdateError, isLoading: isUserUpdating, isSuccess, originalArgs, data } = updateUserReq;

  let backLink = '/';

  if (location.state && location.state.from) {
    backLink = location.state.from;
  }

  const filter = new RegExpMatcher({
    ...englishDataset.build(),
    ...englishRecommendedTransformers,
  });

  const onSubmit = (formData: FormData) => {
    ReactGA.event({
      category: 'Profile',
      action: 'submit',
      label: 'Bio Changed',
    });
    updateUser({ ...user, bio: formData.bio });
  };

  useEffect(() => {
    const bio = watch('bio');

    if (!!errors.bio?.message) {
      setErrorMessage(errors.bio?.message);
    } else if (isUpdateError && originalArgs?.bio === bio) {
      setErrorMessage('bio is taken already');
    } else if (filter.hasMatch(bio)) {
      setErrorMessage('ahh we don’t do that here');
    } else {
      setErrorMessage(null);
    }
  }, [errors, watch('bio'), isUpdateError]);

  useEffect(() => {
    if (isUserUpdating || isUserLoading) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isUserUpdating, isUserLoading]);

  useEffect(() => {
    if (isSuccess && !isUpdateError) {
      setTimeout(() => {
        navigate(backLink, {
          state: {
            bioUpdateStatus: { message: 'Name Changed', isSuccess: !isUpdateError },
          },
        });
      }, 1000);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isSuccess && data) {
      toast({
        duration: 3000,
        isClosable: true,
        position: 'bottom',
        render: ({ onClose }) => {
          return (
            <Flex
              alignItems="center"
              justifyContent="center"
              p="5px 18px !important"
              backgroundColor="#111111"
              borderRadius="10px"
              gap="10px"
              w="min-content"
              whiteSpace="nowrap"
              margin="0 auto 10px"
              onClick={onClose}
            >
              <Box>Bio changed</Box>
            </Flex>
          );
        },
      });
      setTimeout(() => {
        window.history.replaceState({}, document.title);
      }, 4000);
    }
  }, [isSuccess, data]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return (
    <Flex
      flexDirection="column"
      pos="absolute"
      w="100%"
      maxW="500px"
      h="100%"
      backgroundColor="#242424"
      zIndex="999"
      top="0px"
      left="50%"
      p="10px 12px"
      transform="translateX(-50%)"
    >
      <Grid gridTemplateColumns="48px 1fr 48px" gap="8px" alignItems="center" justifyContent="center">
        <Box />
        <Box fontSize="16px" lineHeight="1.2" fontWeight="800" textAlign="center">
          edit bio
        </Box>
        <Box
          as={Link}
          href={backLink}
          display="flex"
          alignItems="center"
          aspectRatio="1/1"
          fontSize="14px"
          lineHeight="1.2"
          fontWeight="500"
          textAlign="right"
          cursor="pointer"
          textDecoration="none !important"
          _active={{
            opacity: '0.5',
            transition: 'opacity 0.2s',
          }}
        >
          close
        </Box>
      </Grid>

      <Flex
        as="form"
        flexDirection="column"
        onSubmit={handleSubmit(onSubmit)}
        gap="24px"
        flex="1"
        justifyContent="flex-start"
        p="0 12px"
        pos="relative"
        mt="18px"
      >
        <Flex flexDirection="column" gap="16px" pos="relative">
          <Flex mb="-8px" fontSize="10px" fontWeight="500" lineHeight="1.2" opacity="0.6" justifyContent="flex-end">
            {watch('bio')?.length ?? 0}/{maxBioLength}
          </Flex>
          <FormControl isInvalid={!!errors.bio}>
            <Textarea
              id="bio"
              placeholder={'check my ig @'}
              onKeyPress={handleKeyPress}
              {...register('bio', {
                required: 'Can’t be empty',
                minLength: { value: 3, message: "it's too short" },
                maxLength: { value: maxBioLength, message: `it’s too long, should be 3-${maxBioLength} characters` },
              })}
              borderColor={isDirty ? '#fff' : '#FFFFFF99'}
              color={watch('bio')?.length > 0 ? '#fff' : '#FFFFFF99'}
              boxShadow="none !important"
              _focus={{ opacity: '1', borderColor: '#fff', outline: 'none' }}
              _focusVisible={{
                opacity: '1',
                borderColor: (!isValid || !!errorMessage) && isDirty ? '#f93c3d' : '#fff',
                outline: 'none',
              }}
            />
          </FormControl>
          <Button
            type="submit"
            isDisabled={!isValid || !!errorMessage || !!data}
            isLoading={isLoading}
            data-cy="submit-button"
          >
            save
          </Button>
          {errorMessage && (
            <SlideFade
              in={true}
              offsetY="-20px"
              style={{ position: 'absolute', top: 'calc(100% + 20px)', textAlign: 'center', width: '100%' }}
            >
              <Box w="100%" color="#f83c3c">
                {errorMessage}
              </Box>
            </SlideFade>
          )}
        </Flex>
      </Flex>
      <Box fontSize="14px" fontWeight="500" lineHeight="1.2" textAlign="center" opacity="0.6" mb="14px">
        shows up in your profile
        <br />
        add your handles for socials
      </Box>
    </Flex>
  );
};

export default EditBio;
